/**
 * 서류 이미지를 고른 형식으로 내려받기(#95) — 브라우저 안에서만 바꾼다(서버·버킷 변경 없음).
 *
 * - PNG/JPEG: 원본이 그 형식이면 그대로, 아니면 canvas 로 다시 굽는다(JPEG 는 흰 바탕 — 투명 칸이 검게 나오지 않게).
 * - PDF: JPEG 를 A4 한 장 가운데에 맞춰 넣은 1쪽 PDF 를 직접 쓴다(라이브러리 없이 — DCTDecode 는 JPEG 바이트를 그대로 담는다).
 */

export type ExportFormat = 'png' | 'jpg' | 'pdf';

async function loadImage(url: string): Promise<HTMLImageElement> {
  const img = new Image();
  img.src = url;
  await img.decode();
  return img;
}

async function canvasBlob(img: HTMLImageElement, type: 'image/png' | 'image/jpeg'): Promise<Blob> {
  const c = document.createElement('canvas');
  c.width = img.naturalWidth;
  c.height = img.naturalHeight;
  const g = c.getContext('2d');
  if (!g) throw new Error('이미지를 바꿀 수 없습니다.');
  if (type === 'image/jpeg') { g.fillStyle = '#fff'; g.fillRect(0, 0, c.width, c.height); }
  g.drawImage(img, 0, 0);
  const blob = await new Promise<Blob | null>((res) => c.toBlob(res, type, 0.92));
  if (!blob) throw new Error('이미지를 바꿀 수 없습니다.');
  return blob;
}

/** JPEG 바이트 하나를 A4(가로·세로는 그림 비율대로) 1쪽 PDF 로. */
export function jpegToPdf(jpeg: Uint8Array, w: number, h: number): Blob {
  const landscape = w > h;
  const pw = landscape ? 842 : 595;
  const ph = landscape ? 595 : 842;
  const margin = 28; // 약 10mm
  const s = Math.min((pw - margin * 2) / w, (ph - margin * 2) / h);
  const dw = +(w * s).toFixed(2);
  const dh = +(h * s).toFixed(2);
  const x = +((pw - dw) / 2).toFixed(2);
  const y = +((ph - dh) / 2).toFixed(2);

  const enc = new TextEncoder();
  const parts: Uint8Array[] = [];
  const offsets: number[] = [];
  let pos = 0;
  const push = (b: Uint8Array) => { parts.push(b); pos += b.length; };
  const text = (t: string) => push(enc.encode(t));
  const obj = (n: number, body: string) => { offsets[n] = pos; text(`${n} 0 obj\n${body}\nendobj\n`); };

  text('%PDF-1.4\n');
  obj(1, '<< /Type /Catalog /Pages 2 0 R >>');
  obj(2, '<< /Type /Pages /Kids [3 0 R] /Count 1 >>');
  obj(3, `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pw} ${ph}] /Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>`);
  offsets[4] = pos;
  text(`4 0 obj\n<< /Type /XObject /Subtype /Image /Width ${w} /Height ${h} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>\nstream\n`);
  push(jpeg);
  text('\nendstream\nendobj\n');
  const content = `q ${dw} 0 0 ${dh} ${x} ${y} cm /Im0 Do Q`;
  obj(5, `<< /Length ${content.length} >>\nstream\n${content}\nendstream`);
  const xref = pos;
  let table = 'xref\n0 6\n0000000000 65535 f \n';
  for (let n = 1; n <= 5; n++) table += `${String(offsets[n]).padStart(10, '0')} 00000 n \n`;
  text(`${table}trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`);
  return new Blob(parts as BlobPart[], { type: 'application/pdf' });
}

/** 열어 둔 서류(blob URL)를 고른 형식으로 바꿔 내려받는다. */
export async function downloadImageAs(url: string, contentType: string, name: string, fmt: ExportFormat): Promise<void> {
  let blob: Blob;
  if (fmt === 'png') {
    blob = contentType === 'image/png' ? await (await fetch(url)).blob() : await canvasBlob(await loadImage(url), 'image/png');
  } else {
    const img = await loadImage(url);
    if (fmt === 'jpg') {
      blob = contentType === 'image/jpeg' ? await (await fetch(url)).blob() : await canvasBlob(img, 'image/jpeg');
    } else {
      // PDF 는 늘 다시 굽는다 — 원본 JPEG 가 CMYK·특이 형식이면 그대로 담았을 때 색이 깨진다(canvas 결과는 RGB 기본형).
      const jpeg = await canvasBlob(img, 'image/jpeg');
      blob = jpegToPdf(new Uint8Array(await jpeg.arrayBuffer()), img.naturalWidth, img.naturalHeight);
    }
  }
  const a = document.createElement('a');
  const href = URL.createObjectURL(blob);
  a.href = href;
  a.download = `${name}.${fmt}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
}

/**
 * 열어 둔 서류(blob URL)를 인쇄한다(#530) — 숨은 iframe 에 그림 한 장만 A4 한 쪽에 맞춰 넣고 인쇄 창을 연다.
 * 가로로 긴 그림은 가로 방향 쪽으로. 화면 다른 부분은 인쇄되지 않는다.
 */
export async function printImage(url: string, title: string): Promise<void> {
  const img = await loadImage(url);
  const landscape = img.naturalWidth > img.naturalHeight;
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
  document.body.appendChild(frame);
  const doc = frame.contentDocument;
  const win = frame.contentWindow;
  if (!doc || !win) { frame.remove(); throw new Error('인쇄 창을 열 수 없습니다.'); }
  const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>
@page { size: A4 ${landscape ? 'landscape' : 'portrait'}; margin: 10mm; }
html, body { margin: 0; height: 100%; }
body { display: flex; align-items: center; justify-content: center; }
img { max-width: 100%; max-height: 100%; object-fit: contain; }
</style></head><body><img src="${esc(url)}" alt=""></body></html>`);
  doc.close();
  const pic = doc.querySelector('img');
  if (pic && !pic.complete) await new Promise<void>((res) => { pic.onload = () => res(); pic.onerror = () => res(); });
  const cleanup = () => setTimeout(() => frame.remove(), 1000);
  win.addEventListener('afterprint', cleanup, { once: true });
  win.focus();
  win.print();
  setTimeout(() => { if (frame.isConnected) frame.remove(); }, 60_000);
}
